import React, { useState, useEffect, useCallback } from 'react';

const USER_BACKEND_URL = 'https://app.loopai.in';

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'AI_GENERATED', label: 'AI Generated' },
  { value: 'REAL_HUMAN', label: 'Real Human' },
  { value: 'CELEBRITY', label: 'Celebrity' },
];
const GENDER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
];
const STYLE_OPTIONS = [
  { value: '', label: 'All Styles' },
  { value: 'realistic', label: 'Realistic' },
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'cartoon', label: 'Cartoon' },
  { value: 'anime', label: 'Anime' },
];
const ETHNICITY_OPTIONS = [
  { value: '', label: 'All Ethnicities' },
  { value: 'INDIAN', label: 'Indian' },
  { value: 'CAUCASIAN', label: 'Caucasian' },
  { value: 'EAST_ASIAN', label: 'East Asian' },
  { value: 'AFRICAN', label: 'African' },
  { value: 'HISPANIC', label: 'Hispanic' },
  { value: 'MIDDLE_EASTERN', label: 'Middle Eastern' },
  { value: 'SOUTHEAST_ASIAN', label: 'Southeast Asian' },
  { value: 'JAPANESE', label: 'Japanese' },
  { value: 'KOREAN', label: 'Korean' },
  { value: 'MEXICAN', label: 'Mexican' },
  { value: 'OTHERS', label: 'Others' },
];

async function fetchActors(filters = {}) {
  const apiKey = sessionStorage.getItem('apiKey');
  const userEmail = sessionStorage.getItem('userEmail');
  const headers = { 'Content-Type': 'application/json' };
  if (apiKey) headers['X-API-Key'] = apiKey;
  if (userEmail) headers['X-User-Email'] = userEmail;

  const params = new URLSearchParams();
  if (filters.category) params.set('category', filters.category);
  if (filters.gender) params.set('gender', filters.gender);
  if (filters.style_type) params.set('style_type', filters.style_type);
  if (filters.ethnicity) params.set('ethnicity', filters.ethnicity);
  if (filters.search) params.set('search', filters.search);
  params.set('limit', '100');

  const qs = params.toString();
  const res = await fetch(`${USER_BACKEND_URL}/api/actors${qs ? `?${qs}` : ''}`, { headers });
  if (!res.ok) throw new Error(`Failed to fetch actors: ${res.status}`);
  const data = await res.json();
  return data.actors || [];
}

export const ActorsLibraryModal = ({ onSelect, onClose }) => {
  const [actors, setActors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedActor, setSelectedActor] = useState(null);

  // Filters
  const [category, setCategory] = useState('');
  const [gender, setGender] = useState('');
  const [styleType, setStyleType] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchActors({ category, gender, style_type: styleType, ethnicity, search: searchDebounce });
      setActors(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [category, gender, styleType, ethnicity, searchDebounce]);

  useEffect(() => { load(); }, [load]);

  const handleSelect = (actor) => {
    const imageUrl = actor.thumbnail_url || actor.image_gallery?.[0] || '';
    onSelect({
      imageUrl,
      name: actor.name,
      description: actor.default_prompt || `${actor.gender || ''} ${actor.ethnicity || ''} ${actor.age_group || ''} actor`.trim(),
      voiceId: actor.voice_id || null,
      actorId: actor.id,
      imageGallery: actor.image_gallery || [],
    });
  };

  const selectCls = 'px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300 focus:border-purple-500 focus:outline-none';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="actors-library-modal">
      <div className="bg-[#111119] rounded-2xl border border-gray-800 w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-800 shrink-0">
          <div>
            <h2 className="text-base font-bold text-white">Actors Library</h2>
            <p className="text-[11px] text-gray-500 mt-0.5">Professional actors with voice clones & reference galleries</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white" data-testid="actors-library-close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 px-5 py-2.5 border-b border-gray-800/60 shrink-0 flex-wrap">
          <div className="relative flex-1 min-w-[160px] max-w-[240px]">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search actors..."
              className="w-full pl-8 pr-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300 placeholder-gray-600 focus:border-purple-500 focus:outline-none"
              data-testid="actors-search" />
          </div>
          <select value={category} onChange={e => setCategory(e.target.value)} className={selectCls} data-testid="actors-filter-category">
            {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={gender} onChange={e => setGender(e.target.value)} className={selectCls} data-testid="actors-filter-gender">
            {GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={styleType} onChange={e => setStyleType(e.target.value)} className={selectCls} data-testid="actors-filter-style">
            {STYLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={ethnicity} onChange={e => setEthnicity(e.target.value)} className={selectCls} data-testid="actors-filter-ethnicity">
            {ETHNICITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="text-[10px] text-gray-600 ml-auto">{actors.length} results</span>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Actor grid */}
          <div className={`overflow-y-auto p-4 ${selectedActor ? 'w-[55%]' : 'w-full'} transition-all`}>
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : error ? (
              <div className="text-center py-10">
                <p className="text-red-400 text-sm mb-2">{error}</p>
                <button onClick={load} className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg">Retry</button>
              </div>
            ) : actors.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-10">No actors found</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {actors.map(actor => {
                  const isSelected = selectedActor?.id === actor.id;
                  return (
                    <button key={actor.id} onClick={() => setSelectedActor(actor)}
                      className={`relative text-left rounded-xl overflow-hidden border transition-all group ${isSelected ? 'border-purple-500 ring-1 ring-purple-500/30' : 'border-gray-800 hover:border-gray-600'}`}
                      data-testid={`actor-card-${actor.id}`}>
                      <div className="aspect-[3/4] bg-gray-900">
                        {actor.thumbnail_url ? (
                          <img src={actor.thumbnail_url} alt={actor.name} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-700">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
                          </div>
                        )}
                      </div>
                      <div className="p-2 bg-[#111119]">
                        <p className="text-xs font-semibold text-gray-200 truncate">{actor.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{actor.category?.replace('_', ' ')} {actor.style_type && `/ ${actor.style_type}`}</p>
                      </div>
                      {/* Badges */}
                      <div className="absolute top-1.5 right-1.5 flex flex-col gap-1">
                        {actor.is_premium && (
                          <span className="px-1.5 py-0.5 bg-amber-500/90 text-[9px] font-bold text-black rounded">PRO</span>
                        )}
                        {actor.voice_id && (
                          <span className="px-1.5 py-0.5 bg-emerald-600/90 text-[9px] font-bold text-white rounded">VOICE</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selectedActor && (
            <div className="w-[45%] border-l border-gray-800 overflow-y-auto p-4 bg-[#0c0c14]" data-testid="actor-detail-panel">
              <div className="space-y-4">
                {/* Thumbnail */}
                <div className="aspect-[3/4] max-h-48 rounded-xl overflow-hidden bg-gray-900 mx-auto w-40">
                  {selectedActor.thumbnail_url ? (
                    <img src={selectedActor.thumbnail_url} alt={selectedActor.name} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600">No image</div>
                  )}
                </div>

                <div className="text-center">
                  <h3 className="text-sm font-bold text-white">{selectedActor.name}</h3>
                  <p className="text-[11px] text-gray-500 mt-0.5">{selectedActor.category?.replace('_', ' ')} {selectedActor.gender && `/ ${selectedActor.gender}`} {selectedActor.age_group && `/ ${selectedActor.age_group}`}</p>
                  {selectedActor.ethnicity && <p className="text-[10px] text-gray-600">{selectedActor.ethnicity} {selectedActor.style_type && `/ ${selectedActor.style_type}`}</p>}
                </div>

                {/* Tags */}
                {selectedActor.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 justify-center">
                    {selectedActor.tags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-gray-800 border border-gray-700 rounded-full text-[10px] text-gray-400">{tag}</span>
                    ))}
                  </div>
                )}

                {/* Gallery */}
                {selectedActor.image_gallery?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Gallery ({selectedActor.image_gallery.length})</p>
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {selectedActor.image_gallery.map((img, i) => (
                        <img key={i} src={img} alt={`${selectedActor.name} ${i+1}`}
                          className="w-16 h-16 rounded-lg object-cover border border-gray-700 shrink-0" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview video */}
                {selectedActor.preview_video_url && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Preview Video</p>
                    <video src={selectedActor.preview_video_url} controls className="w-full rounded-lg border border-gray-700" />
                  </div>
                )}

                {/* Voice sample */}
                {selectedActor.voice_sample_url && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Voice Sample</p>
                    <audio src={selectedActor.voice_sample_url} controls className="w-full h-8" />
                  </div>
                )}

                {/* Credits */}
                {selectedActor.credits > 0 && (
                  <p className="text-center text-[11px] text-amber-400 font-medium">{selectedActor.credits} credits per use</p>
                )}

                {/* Select button */}
                <button onClick={() => handleSelect(selectedActor)}
                  className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors"
                  data-testid="actor-select-btn">
                  Use This Actor
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
